import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  BookOpen,
  Clock,
  Code2,
  Cpu,
  MessageSquare,
  Plus,
  RefreshCw,
} from "lucide-react";

import AppLayout from "../components/AppLayout";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { useConversations } from "../hooks/useConversations";
import client from "../api/client";
import { getCveMetrics, invalidateCves } from "../api/cves";
import { useProjects } from "../hooks/useProjects";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(ts) {
  if (!ts) return null;
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 120) return "0m";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 172800) return "Yesterday";
  return `${Math.floor(secs / 86400)}d ago`;
}

function truncateTitle(s, max = 40) {
  if (!s) return "Untitled conversation";
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CveSeverityChart({ metrics }) {
  // color = bar fill (base semantic token); textColor = count label (the -text variant,
  // darkened per-theme so it clears 4.5:1 as text — e.g. --warning is 1.68:1 on white).
  const bars = [
    { key: "critical", label: "Critical", count: metrics?.critical ?? 0, color: "var(--danger)", textColor: "var(--danger-text)" },
    { key: "high",     label: "High",     count: metrics?.high     ?? 0, color: "var(--severity-high)", textColor: "var(--severity-high-text)" },
    { key: "medium",   label: "Medium",   count: metrics?.medium   ?? 0, color: "var(--warning)", textColor: "var(--warning-text)" },
    { key: "low",      label: "Low",      count: metrics?.low      ?? 0, color: "var(--success)", textColor: "var(--success-text)" },
  ];
  const maxCount = Math.max(...bars.map((b) => b.count), 1);
  return (
    <div className="db-cve-chart-panel">
      <h3 className="db-cve-chart-title">Severity breakdown</h3>
      <div className="db-cve-sev-bars">
        {bars.map((b) => (
          <div key={b.key} className="db-cve-sev-row">
            <span className="db-cve-sev-label">{b.label}</span>
            <div className="db-cve-sev-track">
              <div
                className="db-cve-sev-fill"
                style={{ width: `${(b.count / maxCount) * 100}%`, background: b.color }}
              />
            </div>
            <span className="db-cve-sev-count" style={{ color: b.textColor }}>{b.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CveStatusDonut({ metrics }) {
  const total       = metrics?.total || 1;
  const reviewed    = metrics?.reviewed    ?? 0;
  const pending     = metrics?.pending     ?? 0;
  const whitelisted = metrics?.whitelisted ?? 0;
  const rPct = (reviewed / total) * 100;
  const pPct = (pending  / total) * 100;
  const gradient = `conic-gradient(var(--success) 0% ${rPct}%, var(--chart-neutral) ${rPct}% ${rPct + pPct}%, var(--chart-2) ${rPct + pPct}% 100%)`;
  return (
    <div className="db-cve-chart-panel">
      <h3 className="db-cve-chart-title">Review status</h3>
      <div className="db-cve-donut-wrap">
        <div className="db-cve-donut-ring" style={{ background: gradient }}>
          <div className="db-cve-donut-hole">
            <span className="db-cve-donut-pct">
              {(metrics?.total ?? 0) > 0 ? Math.round((reviewed / metrics.total) * 100) : 0}%
            </span>
            <span className="db-cve-donut-pct-sub">reviewed</span>
          </div>
        </div>
        <ul className="db-cve-donut-legend">
          {[
            { label: "Reviewed",    count: reviewed,    color: "var(--success)" },
            { label: "Pending",     count: pending,     color: "var(--chart-neutral)" },
            { label: "Whitelisted", count: whitelisted, color: "var(--chart-2)" },
          ].map((it) => (
            <li key={it.label} className="db-cve-legend-row">
              <span className="db-cve-legend-dot" style={{ background: it.color }} />
              <span className="db-cve-legend-label">{it.label}</span>
              <span className="db-cve-legend-count" style={{ color: it.color }}>{it.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


function SkeletonCard() {
  return (
    <div className="db-skeleton-card">
      <div className="db-skeleton" style={{ height: 14, width: "65%" }} />
      <div className="db-skeleton" style={{ height: 11, width: "35%" }} />
      <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
        <div className="db-skeleton" style={{ height: 18, width: 30, borderRadius: 0 }} />
        <div className="db-skeleton" style={{ height: 18, width: 30, borderRadius: 0 }} />
        <div className="db-skeleton" style={{ height: 18, width: 30, borderRadius: 0 }} />
      </div>
      <div className="db-skeleton" style={{ height: 26, width: 120, borderRadius: 0, marginTop: "auto" }} />
    </div>
  );
}

function ProjectCard({ project, navigate }) {
  const tech = project.system_type;
  const systemCount = project.systems?.length ?? 0;
  const modules = project.sap_modules ?? [];
  const displayModules = modules.slice(0, 4);
  const overflowCount = modules.length - 4;
  const systemEnvs = [...new Set((project.systems ?? []).map((s) => s.name.toUpperCase()))];
  const activity = timeAgo(project.updated_at);

  return (
    <div className="db-project-card">
      <div className="db-project-card-header">
        <span className="db-project-card-name" title={project.name}>{project.name}</span>
        {tech && (
          <span className={`db-tech-badge db-tech-badge--${tech}`}>
            {tech.toUpperCase()}
          </span>
        )}
      </div>

      <div className="db-project-card-meta">
        {systemCount > 0 && (
          <span>{systemCount} system{systemCount !== 1 ? "s" : ""}</span>
        )}
        {activity && systemCount > 0 && <span className="db-project-card-meta-sep">·</span>}
        {activity && (
          <span className="db-project-card-activity">
            <Clock size={10} />
            {activity}
          </span>
        )}
      </div>

      {modules.length > 0 && (
        <div className="db-project-card-modules">
          {displayModules.map((m) => (
            <span key={m} className="db-module-chip">{m}</span>
          ))}
          {overflowCount > 0 && (
            <span className="db-module-chip db-module-chip--overflow">+{overflowCount}</span>
          )}
        </div>
      )}

      {systemEnvs.length > 0 && (
        <div className="db-system-chips">
          {systemEnvs.slice(0, 5).map((name) => (
            <span key={name} className="db-system-chip">{name}</span>
          ))}
        </div>
      )}

      {project.description ? (
        <p className="db-project-card-desc" title={project.description}>
          {project.description}
        </p>
      ) : (
        <p className="db-project-card-desc db-project-card-desc--empty">No description yet</p>
      )}

      <div className="db-project-card-footer">
        <button
          type="button"
          className="db-chat-project-btn"
          onClick={() => navigate("/chat", { state: { projectId: project.id } })}
        >
          <MessageSquare size={12} />
          Chat with project
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AppHomePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const {
    conversations,
    selectConversation,
    createConversation,
    createDeveloperConversation,
    renameConversation,
    deleteConversation,
  } = useConversations();

  // CVE state
  const [cveMetrics, setCveMetrics] = useState(null);
  const [cveLoading, setCveLoading] = useState(true);
  const [cveError, setCveError]     = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const { projects, loading: projectsLoading } = useProjects();

  // -------------------------------------------------------------------------
  // Load CVEs
  // -------------------------------------------------------------------------

  const loadCves = async (force = false) => {
    if (force) setRefreshing(true);
    else if (cveMetrics === null) setCveLoading(true);
    setCveError(null);
    try {
      if (force) {
        await client.post("/me/cves/sync");
        invalidateCves(user);
      }
      const metrics = await getCveMetrics(user, { revalidate: force });
      setCveMetrics(metrics);
    } catch (err) {
      setCveError(err?.response?.data?.detail ?? "Could not load CVE data.");
    } finally {
      setCveLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { if (user) loadCves(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2 most recent projects by updated_at
  const recentProjects = [...projects]
    .sort((a, b) => {
      const aT = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bT = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return bT - aT;
    })
    .slice(0, 2);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <AppLayout
      mainClassName="grid-bg"
      sidebar={
        <Sidebar
          userName={user?.name || ""}
          userEmail={user?.email || "unknown"}
          conversations={conversations}
          activeConversationId={null}
          onSelectConversation={selectConversation}
          onNewConversation={createConversation}
          onNewDeveloperConversation={createDeveloperConversation}
          onRenameConversation={renameConversation}
          onDeleteConversation={deleteConversation}
          onLogout={logout}
        />
      }
    >
      <div className="page-shell">

        {/* ── Header ── */}
        <header className="page-header">
          <div className="page-header-titles">
            <h1 className="page-title">
              {user?.name ? `Good to see you, ${user.name.split(" ")[0]}` : "Welcome back"}
            </h1>
            <p className="page-subtitle">Your SAP security overview</p>
          </div>
        </header>

        {/* ── Tool strip ── */}
        <div className="db-tools-row">
          <button type="button" className="db-chip-btn db-chip--orange" onClick={() => navigate("/chat")}>
            <Bot size={15} />AI Assistant
          </button>
          <button type="button" className="db-chip-btn db-chip--blue" onClick={() => navigate("/developer")}>
            <Code2 size={15} />Developer Tools
          </button>
          <button type="button" className="db-chip-btn db-chip--purple" onClick={() => navigate("/library")}>
            <BookOpen size={15} />Library
          </button>
          <button type="button" className="db-chip-btn db-chip--teal" onClick={() => navigate("/agents")}>
            <Cpu size={15} />AI Agents
          </button>
        </div>

        {/* ── Zone 1: Recent Projects + Recent Conversations ── */}
        <div className="db-content-zone1">

          {/* Left — Recent Projects */}
          <section className="db-zone-left">
            <div className="db-zone-header">
              <h2 className="db-zone-title">Recent projects</h2>
              <button
                type="button"
                className="db-zone-action-btn"
                onClick={() => navigate("/projects")}
              >
                <Plus size={11} />
                New project
              </button>
            </div>

            {projectsLoading ? (
              <div className="db-project-cards-grid">
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : recentProjects.length === 0 ? (
              <div className="db-zone-empty">
                <p className="db-zone-empty-text">
                  No projects yet — create one to get context-aware AI answers
                </p>
                <button
                  type="button"
                  className="db-zone-empty-btn"
                  onClick={() => navigate("/projects")}
                >
                  Create a project
                </button>
              </div>
            ) : (
              <div className="db-project-cards-grid">
                {recentProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} navigate={navigate} />
                ))}
              </div>
            )}
          </section>

          {/* Right — Recent Conversations */}
          <section className="db-zone-right">
            <div className="db-zone-header">
              <h2 className="db-zone-title">Recent conversations</h2>
              <button
                type="button"
                className="db-zone-action-btn"
                onClick={() => navigate("/chats")}
              >
                View all →
              </button>
            </div>

            {conversations.length === 0 ? (
              <p className="db-recent-conv-empty">No conversations yet.</p>
            ) : (
              <div className="db-recent-conv-list">
                {conversations.slice(0, 5).map((conv) => {
                  const proj = projects.find((p) => p.id === conv.project_id);
                  return (
                    <button
                      key={conv.id}
                      type="button"
                      className="db-conv-row"
                      onClick={() =>
                        conv.type === "developer"
                          ? navigate(`/developer/${conv.id}`)
                          : navigate(`/chat/${conv.id}`)
                      }
                    >
                      <div className="db-conv-row-content">
                        <span className="db-conv-title">{truncateTitle(conv.title)}</span>
                        <div className="db-conv-pills">
                          {conv.type === "developer" ? (
                            <span className="db-conv-pill db-conv-pill--dev">Developer</span>
                          ) : proj ? (
                            <span className="db-conv-pill db-conv-pill--project" title={proj.name}>
                              {proj.name.length > 18 ? proj.name.slice(0, 17) + "…" : proj.name}
                            </span>
                          ) : (
                            <span className="db-conv-pill db-conv-pill--general">General</span>
                          )}
                        </div>
                      </div>
                      <span className="db-conv-time">{timeAgo(conv.created_at)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* ── Zone 2: CVE Dashboard ── */}
        <section className="db-content-zone2">

          <div className="db-zone-header">
            <h2 className="db-zone-title">CVE Feed</h2>
            <div className="db-zone-header-actions">
              <button
                type="button"
                className={`db-refresh-btn${refreshing ? " db-refresh-btn--spin" : ""}`}
                onClick={() => loadCves(true)}
                aria-label="Refresh CVE data"
                title="Force-refresh CVE data from NVD"
              >
                <RefreshCw size={14} />
              </button>
              <button
                type="button"
                className="db-zone-action-btn"
                onClick={() => navigate("/library/cves")}
              >
                View CVE Feed →
              </button>
            </div>
          </div>

          {cveLoading && (
            <div className="db-cve-chart-loading">
              <span className="db-spinner" /> Loading CVE data…
            </div>
          )}

          {cveError && (
            <div className="db-state-placeholder db-state-error">{cveError}</div>
          )}

          {!cveLoading && !cveError && cveMetrics && (
            <div className="db-cve-charts-row">
              <CveSeverityChart metrics={cveMetrics} />
              <CveStatusDonut metrics={cveMetrics} />
            </div>
          )}

        </section>

      </div>
    </AppLayout>
  );
}
